import { Pipe, PipeTransform } from '@angular/core';
import {
  CENTRAL_TIME_ZONE,
  formatCalendarDate,
  formatUserDateTime,
  fromUtcClock,
  type CalendarDateStyle,
  type DateTimeInput,
  type UserDateTimeStyle,
} from '@ups/xplat/utils';

/**
 * Shows an instant (comment created, activity logged, record modified) in the
 * signed-in user's time zone. Use for date-times only; a calendar date with no
 * time goes through `calendarDate` so it is never shifted (WI 35959).
 *
 * `{{ comment.dateCreated | userDateTime: 'short' }}`
 */
@Pipe({ name: 'userDateTime', standalone: true, pure: true })
export class UserDateTimePipe implements PipeTransform {
  transform(
    value: DateTimeInput,
    style: UserDateTimeStyle = 'short',
    timeZone?: string
  ): string {
    return formatUserDateTime(value, style, timeZone);
  }
}

/**
 * Shows an instant on Central (company) time, daylight saving included, for every viewer.
 * Replaces `date:'...':'CST'`, which Angular reads as a fixed -6 h (Bug 37196).
 *
 * `{{ row.DateModified | centralDateTime }}`
 */
@Pipe({ name: 'centralDateTime', standalone: true, pure: true })
export class CentralDateTimePipe implements PipeTransform {
  transform(
    value: DateTimeInput,
    style: UserDateTimeStyle = 'numeric'
  ): string {
    return formatUserDateTime(value, style, CENTRAL_TIME_ZONE);
  }
}

/**
 * Reads a UTC clock that reached the browser with no zone as the instant it
 * stands for (Bug 37590). Chain it before a formatter:
 *
 * `{{ row.DateExported | utcClock | centralDateTime: 'numericDate' }}`
 */
@Pipe({ name: 'utcClock', standalone: true, pure: true })
export class UtcClockPipe implements PipeTransform {
  transform(value: DateTimeInput): Date | null {
    return fromUtcClock(value);
  }
}

/**
 * Shows a calendar date (bid due date, task due date) with no time zone shift.
 *
 * `{{ activity.activityDate | calendarDate: 'mediumDate' }}`
 */
@Pipe({ name: 'calendarDate', standalone: true, pure: true })
export class CalendarDatePipe implements PipeTransform {
  transform(
    value: DateTimeInput,
    style: CalendarDateStyle = 'mediumDate'
  ): string {
    return formatCalendarDate(value, style);
  }
}
